import {defineStore} from 'pinia';
import {AlertsPlusAccessLevel} from "../../types/dto/AlertsPlusAccessLevel";
import {InternationaalAccessLevel} from "../../types/dto/InternationaalAccessLevel";
import {DataMarketingAccessLevel} from "../../types/dto/DataMarketingAccessLevel";
import {RadarAccessLevel, User} from "./user-management/types";
import {Language} from "../../types/dto/Language";
import Ajax from "../../lib/Ajax";
import Confirm from "../../lib/Confirmation";
import translate from "../../lib/Translate";
import { useStore as useMainStore } from '../../main-store';
import Toast from "../../lib/Toast";
export const useAccountStore = defineStore('accountStore', {
    state: () => ({
        email: window.cw.email as string,
        isLoggedInAsAdmin: window.cw.isLoggedInAsAdmin as boolean,
        companyName: window.cw.companyName as string,
        vat: window.cw.vat as string,
        upgradeUsersLink: window.cw.upgradeUsersLink as string,
        avLink: window.cw.AVLink as string,
        hasJoinLink: window.cw.hasJoinLink as boolean,
        joinLink: window.cw.joinLink as string,
        apAccesslevels: window.cw.apAccesslevels as AlertsPlusAccessLevel[],
        irAccesslevels: window.cw.irAccesslevels as InternationaalAccessLevel[],
        dmAccesslevels: window.cw.dmAccesslevels as DataMarketingAccessLevel[],
        radarAccesslevels: window.cw.radarAccesslevels as RadarAccessLevel[],
        userLimit: window.cw.userLimit as number,
        canAddUsers: window.cw.canAddUsers as boolean,
        canSeeShareLink: window.cw.canSeeShareLink as boolean,
        canSeeMobiles: window.cw.canSeeMobiles as boolean,
        users: window.cw.users as User[],
        reviewedUser: null as User | null,
        edittedUser: null as User | null,
        defaultUserLimit: 5,
        languages: window.cw.languages as Language[]
    }),
    getters: {
        activeUsers({users}): User[] {
            return users.filter(u => u.type === "active");
        },
        pendingUsers({users}): User[] {
            return users.filter(u => u.type === "pending");
        },
        needReviewUsers({users}): User[] {
            return users.filter(u => u.type === "review-pending");
        },
        avHtmlLink({avLink}): string {
             return "<a href='" + avLink + "' target='_blank' class='link underline'>" + translate('algemene_voorwaarden') + "</a>";
        },
        userLimitReached({userLimit}): boolean {
            return this.activeUsers.length + this.pendingUsers.length >= userLimit;
        },
        showExtendedUpsellMessage({userLimit, defaultUserLimit}): boolean {
            return userLimit === defaultUserLimit && (this.userLimitReached);
        },
    },
    actions: {
        async deactivateUser(user: User): Promise<void> {
            Confirm(
                async () => {
                    await Ajax.postAsync('/ajax/account/deactivate-user', {userId: user.userId});
                    user.type = "deactivated";
                },
                {
                    title: translate('confirmation_title'),
                    text: translate('confirmation_body'),
                    confirmButtonText: translate('confirmation_ok'),
                    cancelButtonText: translate('confirmation_cancel')
                }
            );
        },
        async reactivateUser(user: User): Promise<void> {
            if (user.hasSuspiciousDomain) {
                const mainstore = useMainStore();
                this.reviewedUser = user;
                mainstore.openModal(mainstore.modals.USERS_REVIEW_USER);
            } else {
                Confirm(
                    async () => {
                        await Ajax.postAsync('/ajax/account/activate-user', { userId: user.userId });
                        user.type = "active";
                    },
                    {
                        title: translate('reactivate_user_confirmation_title'),
                        text: translate('reactivate_user_confirmation_body'),
                        confirmButtonText: translate('reactivate_user_confirmation_ok'),
                        cancelButtonText: translate('confirmation_cancel')
                    }
                );
            }
        },
        async removePendingUser(user: User): Promise<void> {
            Confirm(
                async () => {
                    this.users = this.users.filter(u => u.userId !== user.userId); 
                    await Ajax.postAsync('/ajax/account/remove-pending-user', { userId: user.userId });
                    Toast.success(translate('user_removed', user.name));
                },
                {
                    title: translate("remove_user_confirmation_title"),
                    text: translate("remove_user_confirmation_body"),
                    confirmButtonText: translate("remove_user_confirmation_ok"),
                    cancelButtonText: translate("confirmation_cancel"),
                }
            );
        },
        async acceptUser(user: User): Promise<void> {
            const mainstore = useMainStore();
            try {
                await Ajax.postAsync('/ajax/account/accept-user', {userId: user.userId});
                user.type = "active";
                mainstore.closeModal();
            } catch (e: unknown) {
                Toast.error(e as string);
            }
        },
        async rejectUser(user: User): Promise<void> {
            const mainstore = useMainStore();
            try {
                await Ajax.postAsync('/ajax/account/reject-user', {userId: user.userId});
                user.type = "deactivated";
                mainstore.closeModal();
            } catch (e: unknown) {
                Toast.error(e as string);
            }
        },
        editUser(user: User) {
            if(!this.isLoggedInAsAdmin)
                return;
            
            const mainstore = useMainStore();
            this.edittedUser = user;
            mainstore.openModal(mainstore.modals.USERS_EDIT_USER);
        },
        async savePermissions(user: User): Promise<void> {
            const mainstore = useMainStore();
            await Ajax.postAsync(
                '/ajax/account/setpermissions',
                {
                    userId: user.userId,
                    isAdmin: user.isAdmin,
                    hasVervaldagboek: user.permissions.hasVervaldagboek,
                    alertsPlusAccessLevel: user.permissions.alertsPlusAccessLevel,
                    internationalAccessLevel: user.permissions.internationalAccessLevel,
                    dmAccesslevel: user.permissions.dataMarketingAccessLevel,
                    radarAccesslevel: user.permissions.radarAccessLevel,
                }
            );
            Toast.success(translate('permissions_editted', user.name));
            mainstore.closeModal();
        },
        addUser(userId: number, email: string) {
            this.users.push({
                name: '',
                userId: userId,
                email: email,
                lastActive: 0,
                mobileInstalls: [],
                alertsUsage: 0,
                type: 'pending',
                hasSuspiciousDomain: false,
                isAdmin: false,
                permissions: {
                    hasVervaldagboek: false,
                    alertsPlusAccessLevel: AlertsPlusAccessLevel.NONE,
                    internationalAccessLevel: InternationaalAccessLevel.READONLY,
                    dataMarketingAccessLevel: DataMarketingAccessLevel.FULL,
                    radarAccessLevel: RadarAccessLevel.NONE
                }
            });
        }
    }
});